import React from 'react'
import PropTypes from 'prop-types'
import { lifecycle } from 'recompose'
import Row from '../../../bootstrap/component/Row'
import Column from '../../../bootstrap/component/Column'
import LabelInput from '../../../form/component/LabelInput'
import Input from '../../../form/component/Input'
import DatePicker from '../../../form/component/DatePicker'
import ContentBox from '../../../layout/component/ContentBox'
import Checkbox from '../../../form/component/Checkbox'
import TreatmentPreview from './TreatmentPreview'
import TextArea from '../../../form/component/TextArea'
import ValidationMessageList from '../../../validation/component/ValidationMessageList'
import {
    AVAILABLE_IMPLANT_TREATMENT_PRICE_LIST,
    IMPLANT_TREATMENT_PHYSICIAN_COST,
    CURRENCY_CODE_LIST,
    CURRENCY_CODE_SYMBOL,
} from '../../state/const'
import Select from '../../../form/component/Select'
import {
    getQuotationCommentSelectValues,
    QUOTATION_COMMENT_TEXT,
} from '../../state/quotation/const'
import customerObjectType from '../../../../prop-types/customer-obj-type'
import treatmentQuotationObjectType from '../../../../prop-types/treatment-quotation-obj-type'
import currentUserHasAccess from '../../../../util/widget-helper'
import { IMPLANT_TREATMENT_QUOTATION_WIDGET } from '../../../process/ui-definition/process-ui'
import store from '../../../store'
import { userHasAnyOfRoles } from '../../../security/state/auth/selector'
import {
    ROLE_EMPLOYEE_CUSTOMER_SERVICE, ROLE_QM_TREATMENT_PLANNER, ROLE_TREATMENT_PLANER, ROLE_TREATMENT_QUOTATION_ADMIN,
} from '../../../security/state/auth/const'

const moment = require('moment')

const getProlongValidDate = date =>
    moment(date)
        .add(23, 'hours')
        .add(59, 'minutes')
        .add(59, 'seconds')
        .format('YYYY-MM-DDTHH:mm:ss')

/**
 * this checks if the customer has an @sunshinesmile.de email. If so the purchased state can be reset manually
 * (To allow multiple treatment checkouts for testing)
 * @param customer
 */
const isSunshineSmileCustomer = customer => {
    if (!customer) {
        return false
    }
    if (!customer.email) {
        return false
    }
    return customer.email.indexOf('@sunshinesmile.de') !== -1
}

const getCommentText = (commentKey, countryCode = 'DE') => {
    const getLocalizedText = QUOTATION_COMMENT_TEXT[commentKey]
    if (getLocalizedText) {
        return getLocalizedText(countryCode)
    } else {
        return ''
    }
}

const getComment = ({
    treatmentQuotation,
    changeTreatmentQuotation,
    saveImplantTreatmentQuotation,
    customer,
}) => {
    const commentText = getCommentText(
        treatmentQuotation.commentKey,
        customer.countryCode,
    )
    changeTreatmentQuotation('comment', commentText)
    saveImplantTreatmentQuotation()
}

const currentUserHasReadOnlyRole = () => {
    const canModifyTQRoles = [
        ROLE_EMPLOYEE_CUSTOMER_SERVICE,
        ROLE_TREATMENT_PLANER,
        ROLE_QM_TREATMENT_PLANNER,
        ROLE_TREATMENT_QUOTATION_ADMIN]
    const state = store.getState()
    return !userHasAnyOfRoles(state, canModifyTQRoles)
}

const ImplantTreatmentQuotation = ({
    treatmentQuotation,
    isValid,
    changeTreatmentQuotation,
    saveImplantTreatmentQuotation,
    customer,
    isPreviewModalOpen,
    closePreviewModal,
    validationMessageList,
}) => {
    if (!currentUserHasAccess(IMPLANT_TREATMENT_QUOTATION_WIDGET)) {
        return null
    }

    if (!customer) {
        return null
    }

    if (!customer.isImplantCustomer) {
        return null
    }

    const onChangeSelect = (name, value) => {
        changeTreatmentQuotation(name, value)
        if (name === 'brutPrice') {
            changeTreatmentQuotation('physicianCost', IMPLANT_TREATMENT_PHYSICIAN_COST)
        }
        saveImplantTreatmentQuotation()
    }

    if (treatmentQuotation === null) {
        return <ContentBox title="Implant Treatment Quotation"/>
    }

    const commentExists = treatmentQuotation && treatmentQuotation.commentKey && !treatmentQuotation.comment

    if (commentExists) {
        getComment({
            treatmentQuotation,
            changeTreatmentQuotation,
            saveImplantTreatmentQuotation,
            customer,
        })
    }
    const currencyCodeSymbol = CURRENCY_CODE_SYMBOL[treatmentQuotation.currency]
    const isSunshineSmile = isSunshineSmileCustomer(customer)

    const hasPhysician = !!(
        customer.partnerPhysicianId || customer.treatingPhysicianId
    )
    const readonly = treatmentQuotation.purchased || currentUserHasReadOnlyRole()

    const title = 'Implant Treatment Quotation ' + treatmentQuotation.customerName

    return (
        <ContentBox title={title}>
            <TreatmentPreview
                treatmentQuotation={treatmentQuotation}
                isPreviewModalOpen={isPreviewModalOpen}
                closePreviewModal={closePreviewModal}
            />
            <Row>
                <Column size={12}>
                    <ValidationMessageList
                        validationMessageList={validationMessageList}
                    />
                </Column>
            </Row>

            {isSunshineSmile && (
                <Row>
                    <Column size={6}>
                        <Checkbox
                            object={treatmentQuotation}
                            name="purchased"
                            onChange={onChangeSelect}
                        >
                            Purchased
                        </Checkbox>
                    </Column>
                    <Column size={6}/>
                </Row>
            )}

            <Row>
                <Column size={6}>
                    <Checkbox
                        object={treatmentQuotation}
                        name="approved"
                        readOnly={!isValid || !hasPhysician || readonly}
                        onChange={onChangeSelect}
                    >
                        Allow customer to purchase
                    </Checkbox>
                    {!hasPhysician && (
                        <div className="form-error">
                            Please select a physician to activate this
                            quotation. (in the status area of the Sales Order)
                        </div>
                    )}
                </Column>
                <Column size={6}>
                    <Checkbox
                        object={treatmentQuotation}
                        name="ratePayAllowed"
                        readOnly={readonly}
                        onChange={onChangeSelect}
                    >
                        Rate pay allowed
                    </Checkbox>
                </Column>
            </Row>
            <Row>
                <Column size={6}>
                    <LabelInput label="Valid Until">
                        <DatePicker
                            object={treatmentQuotation}
                            name="validUntil"
                            readOnly={readonly}
                            onChange={(name, value) => {
                                changeTreatmentQuotation(
                                    name,
                                    getProlongValidDate(value),
                                )
                            }}
                            onBlur={saveImplantTreatmentQuotation}
                        />
                    </LabelInput>
                </Column>
            </Row>
            <Row>
                <Column size={6}>
                    <LabelInput
                        label="BrutPrice"
                        groupAddOnIcon={currencyCodeSymbol}
                    >
                        <Select
                            name="brutPrice"
                            object={treatmentQuotation}
                            valueList={AVAILABLE_IMPLANT_TREATMENT_PRICE_LIST}
                            readOnly={readonly}
                            type="int"
                            onChange={onChangeSelect}
                        />
                    </LabelInput>
                </Column>
                <Column size={6}>
                    <LabelInput
                        label="Physician Cost"
                        groupAddOnIcon={currencyCodeSymbol}
                    >
                        <Input
                            object={treatmentQuotation}
                            name="physicianCost"
                            readOnly={true}
                            onChange={changeTreatmentQuotation}
                            onBlur={saveImplantTreatmentQuotation}
                            isMoney={true}
                        />
                    </LabelInput>
                </Column>
            </Row>
            <Row>
                <Column size={6}>
                    <LabelInput label="Comment">
                        <Select
                            name="commentKey"
                            object={treatmentQuotation}
                            valueList={getQuotationCommentSelectValues(false)}
                            readOnly={readonly}
                            addEmpty={true}
                            onChange={(name, value) => {
                                changeTreatmentQuotation(name, value)
                                const commentText = getCommentText(
                                    value,
                                    customer.countryCode,
                                )
                                changeTreatmentQuotation('comment', commentText)
                                saveImplantTreatmentQuotation()
                            }}
                        />
                    </LabelInput>
                </Column>
                <Column size={6}>
                    <LabelInput label="Currency">
                        <Select
                            name="currency"
                            object={treatmentQuotation}
                            valueList={CURRENCY_CODE_LIST}
                            readOnly={readonly}
                            addEmpty={false}
                            onChange={onChangeSelect}
                        />
                    </LabelInput>
                </Column>
            </Row>
            <Row>
                <Column size={12}>
                    <LabelInput label="Comment">
                        <TextArea
                            name="comment"
                            readOnly={readonly}
                            object={treatmentQuotation}
                            onChange={changeTreatmentQuotation}
                            onBlur={saveImplantTreatmentQuotation}
                        />
                    </LabelInput>
                </Column>
            </Row>
        </ContentBox>
    )
}

ImplantTreatmentQuotation.propTypes = {
    treatmentQuotation: treatmentQuotationObjectType,
    isPreviewModalOpen: PropTypes.bool.isRequired,
    isValid: PropTypes.bool,
    findOrCreateTreatmentQuotation: PropTypes.func.isRequired,
    changeTreatmentQuotation: PropTypes.func.isRequired,
    saveImplantTreatmentQuotation: PropTypes.func.isRequired,
    openPreviewModal: PropTypes.func.isRequired,
    closePreviewModal: PropTypes.func.isRequired,
    validationMessageList: PropTypes.array.isRequired,
    customer: customerObjectType,
    productType: PropTypes.string,
}

export default lifecycle({
    componentDidMount () {
        if (this.props.onlyDisplay) {
            return
        }
        if (this.props.customer.isImplantCustomer) {
            this.props.findOrCreateTreatmentQuotation(this.props.productType)
        }
    },
})(ImplantTreatmentQuotation)





// WEBPACK FOOTER //
// ./src/modules/treatment/component/quotation/ImplantTreatmentQuotation.js